import { motion } from "framer-motion";
import { ScanLine, Stethoscope, Layers, BarChart3, Atom, Hexagon, Bot, Compass, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "./Primitives";
import { APP_URL } from "./data";
import { useThemeMode, shotSrc } from "./Theme";

const Card = ({ id, span, icon: Icon, path, title, q, out, children, badge, delay = 0 }) => (
  <Reveal delay={delay} className={`${span} min-w-0`}>
    <motion.a
      href={`${APP_URL.replace(/\/$/, "")}${path}`}
      target="_blank"
      rel="noreferrer"
      data-testid={`module-card-${id}`}
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8 shadow-[0_1px_2px_rgba(17,25,23,0.04)] hover:border-[#1f7a6c]/50 hover:shadow-[0_24px_60px_-28px_rgba(31,122,108,0.45)] transition-[border-color,box-shadow]"
    >
      <div className="flex items-start justify-between">
        <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#1f7a6c]/10 text-[var(--teal)]"><Icon className="h-5 w-5" /></span>
        <span className="flex items-center gap-2 font-mono text-xs text-[var(--muted)]">
          {badge && <span className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 dark:border-amber-400/40 dark:bg-transparent dark:text-amber-300">{badge}</span>}
          {path}
          <ArrowUpRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" />
        </span>
      </div>
      <h3 className="font-display mt-8 text-2xl md:text-3xl font-bold tracking-tight">{title}</h3>
      <p className="mt-3 text-[var(--muted)] leading-relaxed">{q}</p>
      {children}
      <p className="mt-auto pt-6 font-mono text-xs text-[var(--teal)] leading-relaxed">→ {out}</p>
    </motion.a>
  </Reveal>
);

const Shot = ({ src, alt }) => {
  const { dark } = useThemeMode();
  return (
  <div className="relative mt-6 -mx-2 overflow-hidden rounded-lg border border-[var(--line)] aspect-[16/9]">
    <img src={shotSrc(src, dark)} alt={alt} loading="lazy" className="h-full w-full object-cover object-left-top transition-transform duration-700 group-hover:scale-[1.04]" />
    <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface)] via-transparent to-transparent" />
  </div>
  );
};

const Faces = () => (
  <div className="mt-6">
  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Icosahedral cage faces · subdivision levels 0–4</p>
  <div className="mt-3 flex items-end gap-2 font-mono text-xs text-[var(--muted)]">
    {[20, 80, 320, "1,280", "5,120"].map((f, i) => (
      <div key={f} className="flex flex-col items-center gap-2">
        <Hexagon className={i === 3 ? "text-[var(--amber)]" : "text-[var(--teal)]"} style={{ width: 14 + i * 7, height: 14 + i * 7 }} strokeWidth={1.2} />
        <span>{f}</span>
      </div>
    ))}
  </div>
  </div>
);

export const Modules = () => (
  <section id="modules" data-testid="modules-section" className="relative py-24 md:py-32 bg-[var(--surface-2)]">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHead eyebrow="Eight main pages" title={<>One workbench. <br className="hidden sm:block" />Eight precise questions.</>} body="Each page answers one question and produces one kind of output — so you always know what a number is, and what it isn't." />
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
        <Card id="single-audit" span="md:col-span-2 lg:col-span-7" icon={ScanLine} path="/" title="Single Audit" q="Is this chain's local backbone geometry internally consistent?" out="Audit dashboard · 3D view · PDF · bond CSV · JSON">
          <Shot src="/shots/audit.jpg" alt="Single Audit page of the Morphogenesis app" />
        </Card>
        <Card id="triage" span="md:col-span-2 lg:col-span-5" icon={Stethoscope} path="/predict" title="Triage" q="Where do depositions carrying this mutation sit relative to the reference — and what does AlphaMissense annotate?" out="Four-layer structural-QC receipt" delay={0.08}>
          <Shot src="/shots/predict.jpg" alt="Triage page of the Morphogenesis app" />
        </Card>
        <Card id="family-pipeline" span="lg:col-span-4" icon={Layers} path="/family" title="Family Pipeline" q="Which retrieved structures are genuine comparable mutants, and how far are they from the reference?" out="Persistent runs · label checks · exports" />
        <Card id="meta" span="lg:col-span-4" icon={BarChart3} path="/meta" title="Cross-Family Meta" q="How do the eligible family and cohort summaries compare?" out="Aggregate dashboards · selected-family PDF" delay={0.08} />
        <Card id="architect" span="md:col-span-2 lg:col-span-4" icon={Atom} path="/architect" title="Architect" q="Can selected Cα spacings in one structure be regularized toward ≈ 3.80 Å?" out="Live refinement · saved runs · refined PDB" delay={0.16} />
        <Card id="void-shell" span="lg:col-span-5" icon={Hexagon} path="/void-shell" badge="experimental" title="Void Shell" q="Does measured internal empty-space change exceed this gene's observed control variance?" out="Face maps · patch tables · calibrated CSV/JSON">
          <Faces />
        </Card>
        <Card id="sentinel" span="lg:col-span-4" icon={Bot} path="/sentinel" title="Sentinel" q="Can an AI agent gather the structural evidence and prepare a brief — showing every tool receipt?" out="Tool receipts · narrative · linked PDFs" delay={0.08} />
        <Card id="strategist" span="md:col-span-2 lg:col-span-3" icon={Compass} path="/strategist" title="Strategist" q="What does today's evidence imply for research and business priorities?" out="Eight-section memo · Markdown" delay={0.16} />
      </div>
    </div>
  </section>
);
